/**
 * Binance Spot WebSocket API
 *
 * OpenAPI Specifications for the Binance Spot WebSocket API
 *
 * API documents:
 * - [Github web-socket-api documentation file](https://github.com/binance/binance-spot-api-docs/blob/master/web-socket-api.md)
 * - [General API information for web-socket-api on website](https://developers.binance.com/docs/binance-spot-api-docs/web-socket-api/general-api-information)
 *
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { WebsocketAPIBase, WebsocketApiResponse, WebsocketSendMsgOptions } from '@binance/common';
import type {
    OpenOrdersCancelAllResponse,
    OrderAmendKeepPriorityResponse,
    OrderCancelReplaceResponse,
    OrderCancelResponse,
    OrderListCancelResponse,
    OrderListPlaceOcoResponse,
    OrderListPlaceOpoResponse,
    OrderListPlaceOpocoResponse,
    OrderListPlaceOtoResponse,
    OrderListPlaceOtocoResponse,
    OrderListPlaceResponse,
    OrderPlaceResponse,
    OrderTestResponse,
    SorOrderPlaceResponse,
    SorOrderTestResponse,
} from '../types';

/**
 * TradeApi - interface
 *
 * @interface TradeApi
 */
export interface TradeApiInterface {
    /**
     * Cancel all open orders on a symbol.
     * This includes orders that are part of an order list.
     * Weight: 1
     *
     * @summary WebSocket Cancel open orders
     * @param {OpenOrdersCancelAllRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OpenOrdersCancelAllResponse>}
     * @memberof TradeApiInterface
     */
    openOrdersCancelAll(
        requestParameters: OpenOrdersCancelAllRequest
    ): Promise<WebsocketApiResponse<OpenOrdersCancelAllResponse>>;

    /**
     * Reduce the quantity of an existing open order.
     *
     * This adds 0 orders to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [Order Amend Keep Priority FAQ](faqs/order_amend_keep_priority.md) to learn more.
     * Weight: 4
     *
     * @summary WebSocket Order Amend Keep Priority
     * @param {OrderAmendKeepPriorityRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderAmendKeepPriorityResponse>}
     * @memberof TradeApiInterface
     */
    orderAmendKeepPriority(
        requestParameters: OrderAmendKeepPriorityRequest
    ): Promise<WebsocketApiResponse<OrderAmendKeepPriorityResponse>>;

    /**
     * Cancel an active order.
     * Weight: 1
     *
     * @summary WebSocket Cancel order
     * @param {OrderCancelRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderCancelResponse>}
     * @memberof TradeApiInterface
     */
    orderCancel(
        requestParameters: OrderCancelRequest
    ): Promise<WebsocketApiResponse<OrderCancelResponse>>;

    /**
     * * Cancel an existing order and immediately place a new order instead of the canceled one.
     * A new order that was not attempted (i.e. when `newOrderResult: NOT_ATTEMPTED`), will still increase the unfilled order count by 1.
     * You can only cancel an individual order from an orderList using this method, but the result is the same as canceling the entire orderList.
     * Weight: 1
     *
     * @summary WebSocket Cancel and replace order
     * @param {OrderCancelReplaceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderCancelReplaceResponse>}
     * @memberof TradeApiInterface
     */
    orderCancelReplace(
        requestParameters: OrderCancelReplaceRequest
    ): Promise<WebsocketApiResponse<OrderCancelReplaceResponse>>;

    /**
     * Cancel an active order list.
     * Weight: 1
     *
     * @summary WebSocket Cancel Order list
     * @param {OrderListCancelRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListCancelResponse>}
     * @memberof TradeApiInterface
     */
    orderListCancel(
        requestParameters: OrderListCancelRequest
    ): Promise<WebsocketApiResponse<OrderListCancelResponse>>;

    /**
     * Send in a new one-cancels-the-other (OCO) pair:
     * `LIMIT_MAKER` + `STOP_LOSS`/`STOP_LOSS_LIMIT` orders (called *legs*),
     * where activation of one order immediately cancels the other.
     *
     * This adds 1 order to `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter
     * Weight: 1
     *
     * Unfilled Order Count: 1
     *
     * @summary WebSocket Place new OCO - Deprecated
     * @param {OrderListPlaceRequest} requestParameters Request parameters.
     * @deprecated
     * @returns {Promise<OrderListPlaceResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlace(
        requestParameters: OrderListPlaceRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceResponse>>;

    /**
     * Send in an one-cancels-the-other (OCO) pair, where activation of one order immediately cancels the other.
     *
     * An OCO has 2 orders called the **above order** and **below order**.
     * One of the orders must be a `LIMIT_MAKER/TAKE_PROFIT/TAKE_PROFIT_LIMIT` order and the other must be `STOP_LOSS` or `STOP_LOSS_LIMIT` order.
     * Price restrictions:
     * If the OCO is on the `SELL` side:
     * `LIMIT_MAKER/TAKE_PROFIT_LIMIT` `price` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT` `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT stopPrice`
     * If the OCO is on the `BUY` side:
     * `LIMIT_MAKER` `price` < Last Traded Price < `STOP_LOSS/STOP_LOSS_LIMIT` `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT stopPrice`
     * OCOs add **2 orders** to the `EXCHANGE_MAX_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     * Weight: 1
     *
     * Unfilled Order Count: 2
     *
     * @summary WebSocket Place new Order list - OCO
     * @param {OrderListPlaceOcoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOcoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOco(
        requestParameters: OrderListPlaceOcoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOcoResponse>>;

    /**
     * Place an [OPO](./faqs/opo.md).
     *
     * OPOs add 2 orders to the EXCHANGE_MAX_NUM_ORDERS filter and MAX_NUM_ORDERS filter.
     * Weight: 1
     *
     * Unfilled Order Count: 2
     *
     * @summary WebSocket OPO
     * @param {OrderListPlaceOpoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOpoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOpo(
        requestParameters: OrderListPlaceOpoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpoResponse>>;

    /**
     * Place an [OPOCO](./faqs/opo.md).
     * Weight: 1
     *
     * Unfilled Order Count: 3
     *
     * @summary WebSocket OPOCO
     * @param {OrderListPlaceOpocoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOpocoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOpoco(
        requestParameters: OrderListPlaceOpocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpocoResponse>>;

    /**
     * Places an OTO.
     *
     * An OTO (One-Triggers-the-Other) is an order list comprised of 2 orders.
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the working order goes on the order book.
     * The second order is called the **pending order**. It can be any order type except for `MARKET` orders using parameter `quoteOrderQty`. The pending order is only placed on the order book when the working order gets **fully filled**.
     * If either the working order or the pending order is cancelled individually, the other order in the order list will also be canceled or expired.
     * When the order list is placed, if the working order gets **immediately fully filled**, the placement response will show the working order as `FILLED` but the pending order will still appear as `PENDING_NEW`. You need to query the status of the pending order again to see its updated status.
     * OTOs add **2 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     * Weight: 1
     *
     * Unfilled Order Count: 2
     *
     * @summary WebSocket Place new Order list - OTO
     * @param {OrderListPlaceOtoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOtoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOto(
        requestParameters: OrderListPlaceOtoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtoResponse>>;

    /**
     * Place an OTOCO.
     *
     * An OTOCO (One-Triggers-One-Cancels-the-Other) is an order list comprised of 3 orders.
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the working order goes on the order book.
     * The behavior of the working order is the same as the [OTO](#place-new-order-list---oto-trade).
     * OTOCO has 2 pending orders (pending above and pending below), forming an OCO pair. The pending orders are only placed on the order book when the working order gets **fully filled**.
     * The rules of the pending above and pending below follow the same rules as the [Order list OCO](#new-order-list---oco-trade).
     * OTOCOs add **3 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     * Weight: 1
     *
     * Unfilled Order Count: 3
     *
     * @summary WebSocket Place new Order list - OTOCO
     * @param {OrderListPlaceOtocoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListPlaceOtocoResponse>}
     * @memberof TradeApiInterface
     */
    orderListPlaceOtoco(
        requestParameters: OrderListPlaceOtocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtocoResponse>>;

    /**
     * Send in a new order.
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     * Weight: 1
     *
     * @summary WebSocket Place new order
     * @param {OrderPlaceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderPlaceResponse>}
     * @memberof TradeApiInterface
     */
    orderPlace(
        requestParameters: OrderPlaceRequest
    ): Promise<WebsocketApiResponse<OrderPlaceResponse>>;

    /**
     * Test order placement.
     *
     * Validates new order parameters and verifies your signature
     * but does not send the order into the matching engine.
     * Weight: |Condition| Request Weight|
     * |------------           | ------------ |
     * |Without `computeCommissionRates`| 1|
     * |With `computeCommissionRates`|20|
     *
     * @summary WebSocket Test new order
     * @param {OrderTestRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderTestResponse>}
     * @memberof TradeApiInterface
     */
    orderTest(
        requestParameters: OrderTestRequest
    ): Promise<WebsocketApiResponse<OrderTestResponse>>;

    /**
     * Places an order using smart order routing (SOR).
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [SOR FAQ](faqs/sor_faq.md) to learn more.
     * Weight: 1
     *
     * Unfilled Order Count: 1
     *
     * @summary WebSocket Place new order using SOR
     * @param {SorOrderPlaceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<SorOrderPlaceResponse>}
     * @memberof TradeApiInterface
     */
    sorOrderPlace(
        requestParameters: SorOrderPlaceRequest
    ): Promise<WebsocketApiResponse<SorOrderPlaceResponse>>;

    /**
     * Test new order creation and signature/recvWindow using smart order routing (SOR).
     * Creates and validates a new order but does not send it into the matching engine.
     * Weight: |Condition                       | Request Weight|
     * |------------                    | ------------ |
     * |Without `computeCommissionRates`| 1            |
     * |With `computeCommissionRates`   |20            |
     *
     * @summary WebSocket Test new order using SOR
     * @param {SorOrderTestRequest} requestParameters Request parameters.
     *
     * @returns {Promise<SorOrderTestResponse>}
     * @memberof TradeApiInterface
     */
    sorOrderTest(
        requestParameters: SorOrderTestRequest
    ): Promise<WebsocketApiResponse<SorOrderTestResponse>>;
}

/**
 * Request parameters for openOrdersCancelAll operation in TradeApi.
 * @interface OpenOrdersCancelAllRequest
 */
export interface OpenOrdersCancelAllRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOpenOrdersCancelAll
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOpenOrdersCancelAll
     */
    readonly id?: string;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOpenOrdersCancelAll
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderAmendKeepPriority operation in TradeApi.
 * @interface OrderAmendKeepPriorityRequest
 */
export interface OrderAmendKeepPriorityRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly symbol: string;

    /**
     * `newQty` must be greater than 0 and less than the order's quantity.
     * @type {number}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly newQty: number;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly id?: string;

    /**
     * `orderId`or`origClientOrderId`mustbesent
     * @type {number | bigint}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly orderId?: number | bigint;

    /**
     * `orderId`or`origClientOrderId`mustbesent
     * @type {string}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly origClientOrderId?: string;

    /**
     * The new client order ID for the order after being amended. <br> If not sent, one will be randomly generated. <br> It is possible to reuse the current clientOrderId by sending it as the `newClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly newClientOrderId?: string;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderAmendKeepPriority
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderCancel operation in TradeApi.
 * @interface OrderCancelRequest
 */
export interface OrderCancelRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderCancel
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderCancel
     */
    readonly id?: string;

    /**
     * `orderId`or`origClientOrderId`mustbesent
     * @type {number | bigint}
     * @memberof TradeApiOrderCancel
     */
    readonly orderId?: number | bigint;

    /**
     * `orderId`or`origClientOrderId`mustbesent
     * @type {string}
     * @memberof TradeApiOrderCancel
     */
    readonly origClientOrderId?: string;

    /**
     * The new client order ID for the order after being amended. <br> If not sent, one will be randomly generated. <br> It is possible to reuse the current clientOrderId by sending it as the `newClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderCancel
     */
    readonly newClientOrderId?: string;

    /**
     *
     * @type {'ONLY_NEW' | 'NEW' | 'ONLY_PARTIALLY_FILLED' | 'PARTIALLY_FILLED'}
     * @memberof TradeApiOrderCancel
     */
    readonly cancelRestrictions?: OrderCancelCancelRestrictionsEnum;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderCancel
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderCancelReplace operation in TradeApi.
 * @interface OrderCancelReplaceRequest
 */
export interface OrderCancelReplaceRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly symbol: string;

    /**
     *
     * @type {'STOP_ON_FAILURE' | 'ALLOW_FAILURE'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelReplaceMode: OrderCancelReplaceCancelReplaceModeEnum;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly side: OrderCancelReplaceSideEnum;

    /**
     *
     * @type {'MARKET' | 'LIMIT' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly type: OrderCancelReplaceTypeEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly id?: string;

    /**
     * Cancel order by orderId
     * @type {number | bigint}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelOrderId?: number | bigint;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelOrigClientOrderId?: string;

    /**
     * New ID for the canceled order. Automatically generated if not sent
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelNewClientOrderId?: string;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly timeInForce?: OrderCancelReplaceTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly price?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly quantity?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly quoteOrderQty?: number;

    /**
     * The new client order ID for the order after being amended. <br> If not sent, one will be randomly generated. <br> It is possible to reuse the current clientOrderId by sending it as the `newClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly newClientOrderId?: string;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly newOrderRespType?: OrderCancelReplaceNewOrderRespTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly stopPrice?: number;

    /**
     * See Trailing Stop order FAQ
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly trailingDelta?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly icebergQty?: number;

    /**
     * Arbitrary numeric value identifying the order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly strategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the order strategy.
     * Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly strategyType?: number;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly selfTradePreventionMode?: OrderCancelReplaceSelfTradePreventionModeEnum;

    /**
     *
     * @type {'ONLY_NEW' | 'NEW' | 'ONLY_PARTIALLY_FILLED' | 'PARTIALLY_FILLED'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly cancelRestrictions?: OrderCancelReplaceCancelRestrictionsEnum;

    /**
     *
     * @type {'DO_NOTHING' | 'CANCEL_ONLY'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly orderRateLimitExceededMode?: OrderCancelReplaceOrderRateLimitExceededModeEnum;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly pegPriceType?: OrderCancelReplacePegPriceTypeEnum;

    /**
     * Price level to peg the price to (max: 100)
     * See Pegged Orders
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly pegOffsetValue?: number;

    /**
     *
     * @type {'PRICE_LEVEL' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly pegOffsetType?: OrderCancelReplacePegOffsetTypeEnum;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderCancelReplace
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListCancel operation in TradeApi.
 * @interface OrderListCancelRequest
 */
export interface OrderListCancelRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListCancel
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderListCancel
     */
    readonly id?: string;

    /**
     * Cancel order list by orderListId
     * @type {number}
     * @memberof TradeApiOrderListCancel
     */
    readonly orderListId?: number;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListCancel
     */
    readonly listClientOrderId?: string;

    /**
     * The new client order ID for the order after being amended. <br> If not sent, one will be randomly generated. <br> It is possible to reuse the current clientOrderId by sending it as the `newClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderListCancel
     */
    readonly newClientOrderId?: string;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListCancel
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlace operation in TradeApi.
 * @interface OrderListPlaceRequest
 */
export interface OrderListPlaceRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly symbol: string;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlace
     */
    readonly side: OrderListPlaceSideEnum;

    /**
     * Price for the limit order
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly price: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly quantity: number;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly id?: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly listClientOrderId?: string;

    /**
     * Arbitrary unique ID among open orders for the limit order. Automatically generated if not sent
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly limitClientOrderId?: string;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly limitIcebergQty?: number;

    /**
     * Arbitrary numeric value identifying the limit order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlace
     */
    readonly limitStrategyId?: number | bigint;

    /**
     * <p>Arbitrary numeric value identifying the limit order strategy.</p><p>Values smaller than `1000000` are reserved and cannot be used.</p>
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly limitStrategyType?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](faqs/trailing-stop-faq.md)
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly trailingDelta?: number;

    /**
     * Arbitrary unique ID among open orders for the stop order. Automatically generated if not sent
     * @type {string}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopClientOrderId?: string;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopLimitPrice?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopLimitTimeInForce?: OrderListPlaceStopLimitTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopIcebergQty?: number;

    /**
     * Arbitrary numeric value identifying the stop order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopStrategyId?: number | bigint;

    /**
     * <p>Arbitrary numeric value identifying the stop order strategy.</p><p>Values smaller than `1000000` are reserved and cannot be used.</p>
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly stopStrategyType?: number;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiOrderListPlace
     */
    readonly newOrderRespType?: OrderListPlaceNewOrderRespTypeEnum;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderListPlace
     */
    readonly selfTradePreventionMode?: OrderListPlaceSelfTradePreventionModeEnum;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlace
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOco operation in TradeApi.
 * @interface OrderListPlaceOcoRequest
 */
export interface OrderListPlaceOcoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly symbol: string;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly side: OrderListPlaceOcoSideEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly quantity: number;

    /**
     *
     * @type {'STOP_LOSS_LIMIT' | 'STOP_LOSS' | 'LIMIT_MAKER' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveType: OrderListPlaceOcoAboveTypeEnum;

    /**
     *
     * @type {'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowType: OrderListPlaceOcoBelowTypeEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly id?: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly listClientOrderId?: string;

    /**
     * Arbitrary unique ID among open orders for the above order. Automatically generated if not sent
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveClientOrderId?: string;

    /**
     * Note that this can only be used if `aboveTimeInForce` is `GTC`.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveIcebergQty?: number | bigint;

    /**
     * Can be used if `aboveType` is `STOP_LOSS_LIMIT` , `LIMIT_MAKER`, or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly abovePrice?: number;

    /**
     * Can be used if `aboveType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`. <br>Either `aboveStopPrice` or `aboveTrailingDelta` or both, must be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveStopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](faqs/trailing-stop-faq.md).
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveTrailingDelta?: number | bigint;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveTimeInForce?: OrderListPlaceOcoAboveTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the above order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the above order strategy. <br>Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly aboveStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly abovePegPriceType?: OrderListPlaceOcoAbovePegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly abovePegOffsetType?: OrderListPlaceOcoAbovePegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly abovePegOffsetValue?: number;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowClientOrderId?: string;

    /**
     * Note that this can only be used if `belowTimeInForce` is `GTC`.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowIcebergQty?: number | bigint;

    /**
     * Can be used if `belowType` is `STOP_LOSS_LIMIT` , `LIMIT_MAKER`, or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowPrice?: number;

    /**
     * Can be used if `belowType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT` or `TAKE_PROFIT_LIMIT`. <br>Either `belowStopPrice` or `belowTrailingDelta` or both, must be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowStopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](faqs/trailing-stop-faq.md).
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowTrailingDelta?: number | bigint;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowTimeInForce?: OrderListPlaceOcoBelowTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the below order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the below order strategy. <br>Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowPegPriceType?: OrderListPlaceOcoBelowPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowPegOffsetType?: OrderListPlaceOcoBelowPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly belowPegOffsetValue?: number;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly newOrderRespType?: OrderListPlaceOcoNewOrderRespTypeEnum;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly selfTradePreventionMode?: OrderListPlaceOcoSelfTradePreventionModeEnum;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOco
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOpo operation in TradeApi.
 * @interface OrderListPlaceOpoRequest
 */
export interface OrderListPlaceOpoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly symbol: string;

    /**
     *
     * @type {'LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingType: OrderListPlaceOpoWorkingTypeEnum;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingSide: OrderListPlaceOpoWorkingSideEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingPrice: number;

    /**
     * Sets the quantity for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingQuantity: number;

    /**
     *
     * @type {'LIMIT' | 'MARKET' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingType: OrderListPlaceOpoPendingTypeEnum;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingSide: OrderListPlaceOpoPendingSideEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly id?: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly listClientOrderId?: string;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly newOrderRespType?: OrderListPlaceOpoNewOrderRespTypeEnum;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly selfTradePreventionMode?: OrderListPlaceOpoSelfTradePreventionModeEnum;

    /**
     * Arbitrary unique ID among open orders for the working order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingClientOrderId?: string;

    /**
     * This can only be used if `workingTimeInForce` is `GTC`, or if `workingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingTimeInForce?: OrderListPlaceOpoWorkingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the working order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the working order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingPegPriceType?: OrderListPlaceOpoWorkingPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingPegOffsetType?: OrderListPlaceOpoWorkingPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly workingPegOffsetValue?: number;

    /**
     * Arbitrary unique ID among open orders for the pending order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingClientOrderId?: string;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingPrice?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingStopPrice?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingTrailingDelta?: number;

    /**
     * This can only be used if `pendingTimeInForce` is `GTC` or if `pendingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingTimeInForce?: OrderListPlaceOpoPendingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingPegPriceType?: OrderListPlaceOpoPendingPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingPegOffsetType?: OrderListPlaceOpoPendingPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly pendingPegOffsetValue?: number;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpo
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOpoco operation in TradeApi.
 * @interface OrderListPlaceOpocoRequest
 */
export interface OrderListPlaceOpocoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly symbol: string;

    /**
     *
     * @type {'LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingType: OrderListPlaceOpocoWorkingTypeEnum;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingSide: OrderListPlaceOpocoWorkingSideEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingPrice: number;

    /**
     * Sets the quantity for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingQuantity: number;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingSide: OrderListPlaceOpocoPendingSideEnum;

    /**
     *
     * @type {'STOP_LOSS_LIMIT' | 'STOP_LOSS' | 'LIMIT_MAKER' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveType: OrderListPlaceOpocoPendingAboveTypeEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly id?: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly listClientOrderId?: string;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly newOrderRespType?: OrderListPlaceOpocoNewOrderRespTypeEnum;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly selfTradePreventionMode?: OrderListPlaceOpocoSelfTradePreventionModeEnum;

    /**
     * Arbitrary unique ID among open orders for the working order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingClientOrderId?: string;

    /**
     * This can only be used if `workingTimeInForce` is `GTC`, or if `workingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingTimeInForce?: OrderListPlaceOpocoWorkingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the working order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the working order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingPegPriceType?: OrderListPlaceOpocoWorkingPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingPegOffsetType?: OrderListPlaceOpocoWorkingPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly workingPegOffsetValue?: number;

    /**
     * Arbitrary unique ID among open orders for the pending above order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveClientOrderId?: string;

    /**
     * Can be used if `pendingAboveType` is `STOP_LOSS_LIMIT` , `LIMIT_MAKER`, or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAbovePrice?: number;

    /**
     * Can be used if `pendingAboveType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveStopPrice?: number;

    /**
     * See [Trailing Stop FAQ](./faqs/trailing-stop-faq.md)
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveTrailingDelta?: number;

    /**
     * This can only be used if `pendingAboveTimeInForce` is `GTC` or if `pendingAboveType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveTimeInForce?: OrderListPlaceOpocoPendingAboveTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending above order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending above order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAboveStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAbovePegPriceType?: OrderListPlaceOpocoPendingAbovePegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAbovePegOffsetType?: OrderListPlaceOpocoPendingAbovePegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingAbovePegOffsetValue?: number;

    /**
     *
     * @type {'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowType?: OrderListPlaceOpocoPendingBelowTypeEnum;

    /**
     * Arbitrary unique ID among open orders for the pending below order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowClientOrderId?: string;

    /**
     * Can be used if `pendingBelowType` is `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT` to specify limit price
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowPrice?: number;

    /**
     * Can be used if `pendingBelowType` is `STOP_LOSS`, `STOP_LOSS_LIMIT, TAKE_PROFIT or TAKE_PROFIT_LIMIT`. Either `pendingBelowStopPrice` or `pendingBelowTrailingDelta` or both, must be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowStopPrice?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowTrailingDelta?: number;

    /**
     * This can only be used if `pendingBelowTimeInForce` is `GTC`, or if `pendingBelowType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowTimeInForce?: OrderListPlaceOpocoPendingBelowTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending below order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending below order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowPegPriceType?: OrderListPlaceOpocoPendingBelowPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowPegOffsetType?: OrderListPlaceOpocoPendingBelowPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly pendingBelowPegOffsetValue?: number;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOpoco
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOto operation in TradeApi.
 * @interface OrderListPlaceOtoRequest
 */
export interface OrderListPlaceOtoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly symbol: string;

    /**
     *
     * @type {'LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingType: OrderListPlaceOtoWorkingTypeEnum;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingSide: OrderListPlaceOtoWorkingSideEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingPrice: number;

    /**
     * Sets the quantity for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingQuantity: number;

    /**
     *
     * @type {'LIMIT' | 'MARKET' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingType: OrderListPlaceOtoPendingTypeEnum;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingSide: OrderListPlaceOtoPendingSideEnum;

    /**
     * Sets the quantity for the pending order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingQuantity: number;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly id?: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly listClientOrderId?: string;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly newOrderRespType?: OrderListPlaceOtoNewOrderRespTypeEnum;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly selfTradePreventionMode?: OrderListPlaceOtoSelfTradePreventionModeEnum;

    /**
     * Arbitrary unique ID among open orders for the working order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingClientOrderId?: string;

    /**
     * This can only be used if `workingTimeInForce` is `GTC`, or if `workingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingTimeInForce?: OrderListPlaceOtoWorkingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the working order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the working order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingPegPriceType?: OrderListPlaceOtoWorkingPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingPegOffsetType?: OrderListPlaceOtoWorkingPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly workingPegOffsetValue?: number;

    /**
     * Arbitrary unique ID among open orders for the pending order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingClientOrderId?: string;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingPrice?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingStopPrice?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingTrailingDelta?: number;

    /**
     * This can only be used if `pendingTimeInForce` is `GTC` or if `pendingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingTimeInForce?: OrderListPlaceOtoPendingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingStrategyType?: number;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingPegOffsetType?: OrderListPlaceOtoPendingPegOffsetTypeEnum;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingPegPriceType?: OrderListPlaceOtoPendingPegPriceTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly pendingPegOffsetValue?: number;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOto
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListPlaceOtoco operation in TradeApi.
 * @interface OrderListPlaceOtocoRequest
 */
export interface OrderListPlaceOtocoRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly symbol: string;

    /**
     *
     * @type {'LIMIT' | 'LIMIT_MAKER'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingType: OrderListPlaceOtocoWorkingTypeEnum;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingSide: OrderListPlaceOtocoWorkingSideEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingPrice: number;

    /**
     * Sets the quantity for the working order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingQuantity: number;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingSide: OrderListPlaceOtocoPendingSideEnum;

    /**
     * Sets the quantity for the pending order.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingQuantity: number;

    /**
     *
     * @type {'STOP_LOSS_LIMIT' | 'STOP_LOSS' | 'LIMIT_MAKER' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveType: OrderListPlaceOtocoPendingAboveTypeEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly id?: string;

    /**
     *
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly listClientOrderId?: string;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly newOrderRespType?: OrderListPlaceOtocoNewOrderRespTypeEnum;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly selfTradePreventionMode?: OrderListPlaceOtocoSelfTradePreventionModeEnum;

    /**
     * Arbitrary unique ID among open orders for the working order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingClientOrderId?: string;

    /**
     * This can only be used if `workingTimeInForce` is `GTC`, or if `workingType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingTimeInForce?: OrderListPlaceOtocoWorkingTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the working order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the working order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingPegPriceType?: OrderListPlaceOtocoWorkingPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingPegOffsetType?: OrderListPlaceOtocoWorkingPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly workingPegOffsetValue?: number;

    /**
     * Arbitrary unique ID among open orders for the pending above order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveClientOrderId?: string;

    /**
     * Can be used if `pendingAboveType` is `STOP_LOSS_LIMIT` , `LIMIT_MAKER`, or `TAKE_PROFIT_LIMIT` to specify the limit price.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAbovePrice?: number;

    /**
     * Can be used if `pendingAboveType` is `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT`, `TAKE_PROFIT_LIMIT`
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveStopPrice?: number;

    /**
     * See [Trailing Stop FAQ](./faqs/trailing-stop-faq.md)
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveTrailingDelta?: number;

    /**
     * This can only be used if `pendingAboveTimeInForce` is `GTC` or if `pendingAboveType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveTimeInForce?: OrderListPlaceOtocoPendingAboveTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending above order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending above order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAboveStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAbovePegPriceType?: OrderListPlaceOtocoPendingAbovePegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAbovePegOffsetType?: OrderListPlaceOtocoPendingAbovePegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingAbovePegOffsetValue?: number;

    /**
     *
     * @type {'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowType?: OrderListPlaceOtocoPendingBelowTypeEnum;

    /**
     * Arbitrary unique ID among open orders for the pending below order. Automatically generated if not sent.
     * @type {string}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowClientOrderId?: string;

    /**
     * Can be used if `pendingBelowType` is `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT` to specify limit price
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowPrice?: number;

    /**
     * Can be used if `pendingBelowType` is `STOP_LOSS`, `STOP_LOSS_LIMIT, TAKE_PROFIT or TAKE_PROFIT_LIMIT`. Either `pendingBelowStopPrice` or `pendingBelowTrailingDelta` or both, must be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowStopPrice?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowTrailingDelta?: number;

    /**
     * This can only be used if `pendingBelowTimeInForce` is `GTC`, or if `pendingBelowType` is `LIMIT_MAKER`.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowIcebergQty?: number;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowTimeInForce?: OrderListPlaceOtocoPendingBelowTimeInForceEnum;

    /**
     * Arbitrary numeric value identifying the pending below order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowStrategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the pending below order strategy. Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowStrategyType?: number;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowPegPriceType?: OrderListPlaceOtocoPendingBelowPegPriceTypeEnum;

    /**
     *
     * @type {'PRICE_LEVEL'}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowPegOffsetType?: OrderListPlaceOtocoPendingBelowPegOffsetTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly pendingBelowPegOffsetValue?: number;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderListPlaceOtoco
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderPlace operation in TradeApi.
 * @interface OrderPlaceRequest
 */
export interface OrderPlaceRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderPlace
     */
    readonly symbol: string;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderPlace
     */
    readonly side: OrderPlaceSideEnum;

    /**
     *
     * @type {'MARKET' | 'LIMIT' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderPlace
     */
    readonly type: OrderPlaceTypeEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderPlace
     */
    readonly id?: string;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderPlace
     */
    readonly timeInForce?: OrderPlaceTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly price?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly quantity?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly quoteOrderQty?: number;

    /**
     * The new client order ID for the order after being amended. <br> If not sent, one will be randomly generated. <br> It is possible to reuse the current clientOrderId by sending it as the `newClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderPlace
     */
    readonly newClientOrderId?: string;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiOrderPlace
     */
    readonly newOrderRespType?: OrderPlaceNewOrderRespTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly stopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](faqs/trailing-stop-faq.md)
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly trailingDelta?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly icebergQty?: number;

    /**
     * Arbitrary numeric value identifying the order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderPlace
     */
    readonly strategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the order strategy.
     * Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly strategyType?: number;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderPlace
     */
    readonly selfTradePreventionMode?: OrderPlaceSelfTradePreventionModeEnum;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderPlace
     */
    readonly pegPriceType?: OrderPlacePegPriceTypeEnum;

    /**
     * Price level to peg the price to (max: 100)
     * See Pegged Orders
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly pegOffsetValue?: number;

    /**
     *
     * @type {'PRICE_LEVEL' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderPlace
     */
    readonly pegOffsetType?: OrderPlacePegOffsetTypeEnum;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderPlace
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderTest operation in TradeApi.
 * @interface OrderTestRequest
 */
export interface OrderTestRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiOrderTest
     */
    readonly symbol: string;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiOrderTest
     */
    readonly side: OrderTestSideEnum;

    /**
     *
     * @type {'MARKET' | 'LIMIT' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderTest
     */
    readonly type: OrderTestTypeEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiOrderTest
     */
    readonly id?: string;

    /**
     * Default: `false` <br> See [Commissions FAQ](faqs/commission_faq.md#test-order-diferences) to learn more.
     * @type {boolean}
     * @memberof TradeApiOrderTest
     */
    readonly computeCommissionRates?: boolean;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderTest
     */
    readonly timeInForce?: OrderTestTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly price?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly quantity?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly quoteOrderQty?: number;

    /**
     * The new client order ID for the order after being amended. <br> If not sent, one will be randomly generated. <br> It is possible to reuse the current clientOrderId by sending it as the `newClientOrderId`.
     * @type {string}
     * @memberof TradeApiOrderTest
     */
    readonly newClientOrderId?: string;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiOrderTest
     */
    readonly newOrderRespType?: OrderTestNewOrderRespTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly stopPrice?: number;

    /**
     * See [Trailing Stop order FAQ](faqs/trailing-stop-faq.md)
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly trailingDelta?: number;

    /**
     *
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly icebergQty?: number;

    /**
     * Arbitrary numeric value identifying the order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiOrderTest
     */
    readonly strategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the order strategy.
     * Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly strategyType?: number;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderTest
     */
    readonly selfTradePreventionMode?: OrderTestSelfTradePreventionModeEnum;

    /**
     *
     * @type {'PRIMARY_PEG' | 'MARKET_PEG' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderTest
     */
    readonly pegPriceType?: OrderTestPegPriceTypeEnum;

    /**
     * Price level to peg the price to (max: 100)
     * See Pegged Orders
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly pegOffsetValue?: number;

    /**
     *
     * @type {'PRICE_LEVEL' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiOrderTest
     */
    readonly pegOffsetType?: OrderTestPegOffsetTypeEnum;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiOrderTest
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for sorOrderPlace operation in TradeApi.
 * @interface SorOrderPlaceRequest
 */
export interface SorOrderPlaceRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiSorOrderPlace
     */
    readonly symbol: string;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly side: SorOrderPlaceSideEnum;

    /**
     *
     * @type {'MARKET' | 'LIMIT' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly type: SorOrderPlaceTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly quantity: number;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiSorOrderPlace
     */
    readonly id?: string;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly timeInForce?: SorOrderPlaceTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly price?: number;

    /**
     * The new client order ID for the order after being amended. <br> If not sent, one will be randomly generated. <br> It is possible to reuse the current clientOrderId by sending it as the `newClientOrderId`.
     * @type {string}
     * @memberof TradeApiSorOrderPlace
     */
    readonly newClientOrderId?: string;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly newOrderRespType?: SorOrderPlaceNewOrderRespTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly icebergQty?: number;

    /**
     * Arbitrary numeric value identifying the order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiSorOrderPlace
     */
    readonly strategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the order strategy.
     * Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly strategyType?: number;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiSorOrderPlace
     */
    readonly selfTradePreventionMode?: SorOrderPlaceSelfTradePreventionModeEnum;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiSorOrderPlace
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for sorOrderTest operation in TradeApi.
 * @interface SorOrderTestRequest
 */
export interface SorOrderTestRequest {
    /**
     *
     * @type {string}
     * @memberof TradeApiSorOrderTest
     */
    readonly symbol: string;

    /**
     *
     * @type {'BUY' | 'SELL'}
     * @memberof TradeApiSorOrderTest
     */
    readonly side: SorOrderTestSideEnum;

    /**
     *
     * @type {'MARKET' | 'LIMIT' | 'STOP_LOSS' | 'STOP_LOSS_LIMIT' | 'TAKE_PROFIT' | 'TAKE_PROFIT_LIMIT' | 'LIMIT_MAKER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiSorOrderTest
     */
    readonly type: SorOrderTestTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly quantity: number;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof TradeApiSorOrderTest
     */
    readonly id?: string;

    /**
     * Default: `false` <br> See [Commissions FAQ](faqs/commission_faq.md#test-order-diferences) to learn more.
     * @type {boolean}
     * @memberof TradeApiSorOrderTest
     */
    readonly computeCommissionRates?: boolean;

    /**
     *
     * @type {'GTC' | 'IOC' | 'FOK' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiSorOrderTest
     */
    readonly timeInForce?: SorOrderTestTimeInForceEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly price?: number;

    /**
     * The new client order ID for the order after being amended. <br> If not sent, one will be randomly generated. <br> It is possible to reuse the current clientOrderId by sending it as the `newClientOrderId`.
     * @type {string}
     * @memberof TradeApiSorOrderTest
     */
    readonly newClientOrderId?: string;

    /**
     *
     * @type {'ACK' | 'RESULT' | 'FULL' | 'MARKET' | 'LIMIT'}
     * @memberof TradeApiSorOrderTest
     */
    readonly newOrderRespType?: SorOrderTestNewOrderRespTypeEnum;

    /**
     *
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly icebergQty?: number;

    /**
     * Arbitrary numeric value identifying the order within an order strategy.
     * @type {number | bigint}
     * @memberof TradeApiSorOrderTest
     */
    readonly strategyId?: number | bigint;

    /**
     * Arbitrary numeric value identifying the order strategy.
     * Values smaller than 1000000 are reserved and cannot be used.
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly strategyType?: number;

    /**
     *
     * @type {'NONE' | 'EXPIRE_TAKER' | 'EXPIRE_MAKER' | 'EXPIRE_BOTH' | 'DECREMENT' | 'TRANSFER' | 'NON_REPRESENTABLE'}
     * @memberof TradeApiSorOrderTest
     */
    readonly selfTradePreventionMode?: SorOrderTestSelfTradePreventionModeEnum;

    /**
     * The value cannot be greater than `60000`. <br> Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof TradeApiSorOrderTest
     */
    readonly recvWindow?: number;
}

/**
 * TradeApi - object-oriented interface
 * @class TradeApi
 * @extends {WebsocketAPIBase}
 */
export class TradeApi implements TradeApiInterface {
    private readonly websocketBase: WebsocketAPIBase;

    constructor(websocketBase: WebsocketAPIBase) {
        this.websocketBase = websocketBase;
    }

    /**
     * Cancel all open orders on a symbol.
     * This includes orders that are part of an order list.
     * Weight: 1
     *
     * @summary WebSocket Cancel open orders
     * @param {OpenOrdersCancelAllRequest} requestParameters Request parameters.
     * @returns {Promise<OpenOrdersCancelAllResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#cancel-open-orders-trade Binance API Documentation}
     */
    public openOrdersCancelAll(
        requestParameters: OpenOrdersCancelAllRequest
    ): Promise<WebsocketApiResponse<OpenOrdersCancelAllResponse>> {
        return this.websocketBase.sendMessage<OpenOrdersCancelAllResponse>(
            '/openOrders.cancelAll'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Reduce the quantity of an existing open order.
     *
     * This adds 0 orders to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [Order Amend Keep Priority FAQ](faqs/order_amend_keep_priority.md) to learn more.
     * Weight: 4
     *
     * @summary WebSocket Order Amend Keep Priority
     * @param {OrderAmendKeepPriorityRequest} requestParameters Request parameters.
     * @returns {Promise<OrderAmendKeepPriorityResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#order-amend-keep-priority-trade Binance API Documentation}
     */
    public orderAmendKeepPriority(
        requestParameters: OrderAmendKeepPriorityRequest
    ): Promise<WebsocketApiResponse<OrderAmendKeepPriorityResponse>> {
        return this.websocketBase.sendMessage<OrderAmendKeepPriorityResponse>(
            '/order.amend.keepPriority'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Cancel an active order.
     * Weight: 1
     *
     * @summary WebSocket Cancel order
     * @param {OrderCancelRequest} requestParameters Request parameters.
     * @returns {Promise<OrderCancelResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#cancel-order-trade Binance API Documentation}
     */
    public orderCancel(
        requestParameters: OrderCancelRequest
    ): Promise<WebsocketApiResponse<OrderCancelResponse>> {
        return this.websocketBase.sendMessage<OrderCancelResponse>(
            '/order.cancel'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * * Cancel an existing order and immediately place a new order instead of the canceled one.
     * A new order that was not attempted (i.e. when `newOrderResult: NOT_ATTEMPTED`), will still increase the unfilled order count by 1.
     * You can only cancel an individual order from an orderList using this method, but the result is the same as canceling the entire orderList.
     * Weight: 1
     *
     * @summary WebSocket Cancel and replace order
     * @param {OrderCancelReplaceRequest} requestParameters Request parameters.
     * @returns {Promise<OrderCancelReplaceResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#cancel-and-replace-order-trade Binance API Documentation}
     */
    public orderCancelReplace(
        requestParameters: OrderCancelReplaceRequest
    ): Promise<WebsocketApiResponse<OrderCancelReplaceResponse>> {
        return this.websocketBase.sendMessage<OrderCancelReplaceResponse>(
            '/order.cancelReplace'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Cancel an active order list.
     * Weight: 1
     *
     * @summary WebSocket Cancel Order list
     * @param {OrderListCancelRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListCancelResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#cancel-order-list-trade Binance API Documentation}
     */
    public orderListCancel(
        requestParameters: OrderListCancelRequest
    ): Promise<WebsocketApiResponse<OrderListCancelResponse>> {
        return this.websocketBase.sendMessage<OrderListCancelResponse>(
            '/orderList.cancel'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Send in a new one-cancels-the-other (OCO) pair:
     * `LIMIT_MAKER` + `STOP_LOSS`/`STOP_LOSS_LIMIT` orders (called *legs*),
     * where activation of one order immediately cancels the other.
     *
     * This adds 1 order to `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter
     * Weight: 1
     *
     * Unfilled Order Count: 1
     *
     * @summary WebSocket Place new OCO - Deprecated
     * @param {OrderListPlaceRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#place-new-oco---deprecated-trade Binance API Documentation}
     */
    public orderListPlace(
        requestParameters: OrderListPlaceRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceResponse>(
            '/orderList.place'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Send in an one-cancels-the-other (OCO) pair, where activation of one order immediately cancels the other.
     *
     * An OCO has 2 orders called the **above order** and **below order**.
     * One of the orders must be a `LIMIT_MAKER/TAKE_PROFIT/TAKE_PROFIT_LIMIT` order and the other must be `STOP_LOSS` or `STOP_LOSS_LIMIT` order.
     * Price restrictions:
     * If the OCO is on the `SELL` side:
     * `LIMIT_MAKER/TAKE_PROFIT_LIMIT` `price` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT` `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT stopPrice`
     * If the OCO is on the `BUY` side:
     * `LIMIT_MAKER` `price` < Last Traded Price < `STOP_LOSS/STOP_LOSS_LIMIT` `stopPrice`
     * `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT stopPrice`
     * OCOs add **2 orders** to the `EXCHANGE_MAX_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     * Weight: 1
     *
     * Unfilled Order Count: 2
     *
     * @summary WebSocket Place new Order list - OCO
     * @param {OrderListPlaceOcoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOcoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#place-new-order-list---oco-trade Binance API Documentation}
     */
    public orderListPlaceOco(
        requestParameters: OrderListPlaceOcoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOcoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOcoResponse>(
            '/orderList.place.oco'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Place an [OPO](./faqs/opo.md).
     *
     * OPOs add 2 orders to the EXCHANGE_MAX_NUM_ORDERS filter and MAX_NUM_ORDERS filter.
     * Weight: 1
     *
     * Unfilled Order Count: 2
     *
     * @summary WebSocket OPO
     * @param {OrderListPlaceOpoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOpoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#opo-trade Binance API Documentation}
     */
    public orderListPlaceOpo(
        requestParameters: OrderListPlaceOpoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOpoResponse>(
            '/orderList.place.opo'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Place an [OPOCO](./faqs/opo.md).
     * Weight: 1
     *
     * Unfilled Order Count: 3
     *
     * @summary WebSocket OPOCO
     * @param {OrderListPlaceOpocoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOpocoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#opoco-trade Binance API Documentation}
     */
    public orderListPlaceOpoco(
        requestParameters: OrderListPlaceOpocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOpocoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOpocoResponse>(
            '/orderList.place.opoco'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Places an OTO.
     *
     * An OTO (One-Triggers-the-Other) is an order list comprised of 2 orders.
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the working order goes on the order book.
     * The second order is called the **pending order**. It can be any order type except for `MARKET` orders using parameter `quoteOrderQty`. The pending order is only placed on the order book when the working order gets **fully filled**.
     * If either the working order or the pending order is cancelled individually, the other order in the order list will also be canceled or expired.
     * When the order list is placed, if the working order gets **immediately fully filled**, the placement response will show the working order as `FILLED` but the pending order will still appear as `PENDING_NEW`. You need to query the status of the pending order again to see its updated status.
     * OTOs add **2 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     * Weight: 1
     *
     * Unfilled Order Count: 2
     *
     * @summary WebSocket Place new Order list - OTO
     * @param {OrderListPlaceOtoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOtoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#place-new-order-list---oto-trade Binance API Documentation}
     */
    public orderListPlaceOto(
        requestParameters: OrderListPlaceOtoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOtoResponse>(
            '/orderList.place.oto'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Place an OTOCO.
     *
     * An OTOCO (One-Triggers-One-Cancels-the-Other) is an order list comprised of 3 orders.
     * The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the working order goes on the order book.
     * The behavior of the working order is the same as the [OTO](#place-new-order-list---oto-trade).
     * OTOCO has 2 pending orders (pending above and pending below), forming an OCO pair. The pending orders are only placed on the order book when the working order gets **fully filled**.
     * The rules of the pending above and pending below follow the same rules as the [Order list OCO](#new-order-list---oco-trade).
     * OTOCOs add **3 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     * Weight: 1
     *
     * Unfilled Order Count: 3
     *
     * @summary WebSocket Place new Order list - OTOCO
     * @param {OrderListPlaceOtocoRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListPlaceOtocoResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#place-new-order-list---otoco-trade Binance API Documentation}
     */
    public orderListPlaceOtoco(
        requestParameters: OrderListPlaceOtocoRequest
    ): Promise<WebsocketApiResponse<OrderListPlaceOtocoResponse>> {
        return this.websocketBase.sendMessage<OrderListPlaceOtocoResponse>(
            '/orderList.place.otoco'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Send in a new order.
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     * Weight: 1
     *
     * @summary WebSocket Place new order
     * @param {OrderPlaceRequest} requestParameters Request parameters.
     * @returns {Promise<OrderPlaceResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#place-new-order-trade Binance API Documentation}
     */
    public orderPlace(
        requestParameters: OrderPlaceRequest
    ): Promise<WebsocketApiResponse<OrderPlaceResponse>> {
        return this.websocketBase.sendMessage<OrderPlaceResponse>(
            '/order.place'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Test order placement.
     *
     * Validates new order parameters and verifies your signature
     * but does not send the order into the matching engine.
     * Weight: |Condition| Request Weight|
     * |------------           | ------------ |
     * |Without `computeCommissionRates`| 1|
     * |With `computeCommissionRates`|20|
     *
     * @summary WebSocket Test new order
     * @param {OrderTestRequest} requestParameters Request parameters.
     * @returns {Promise<OrderTestResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#test-new-order-trade Binance API Documentation}
     */
    public orderTest(
        requestParameters: OrderTestRequest
    ): Promise<WebsocketApiResponse<OrderTestResponse>> {
        return this.websocketBase.sendMessage<OrderTestResponse>(
            '/order.test'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Places an order using smart order routing (SOR).
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [SOR FAQ](faqs/sor_faq.md) to learn more.
     * Weight: 1
     *
     * Unfilled Order Count: 1
     *
     * @summary WebSocket Place new order using SOR
     * @param {SorOrderPlaceRequest} requestParameters Request parameters.
     * @returns {Promise<SorOrderPlaceResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#place-new-order-using-sor-trade Binance API Documentation}
     */
    public sorOrderPlace(
        requestParameters: SorOrderPlaceRequest
    ): Promise<WebsocketApiResponse<SorOrderPlaceResponse>> {
        return this.websocketBase.sendMessage<SorOrderPlaceResponse>(
            '/sor.order.place'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Test new order creation and signature/recvWindow using smart order routing (SOR).
     * Creates and validates a new order but does not send it into the matching engine.
     * Weight: |Condition                       | Request Weight|
     * |------------                    | ------------ |
     * |Without `computeCommissionRates`| 1            |
     * |With `computeCommissionRates`   |20            |
     *
     * @summary WebSocket Test new order using SOR
     * @param {SorOrderTestRequest} requestParameters Request parameters.
     * @returns {Promise<SorOrderTestResponse>}
     * @memberof TradeApi
     * @see {@link https://developers.binance.com/docs/binance-spot-api-docs/websocket-api/trading-requests#test-new-order-using-sor-trade Binance API Documentation}
     */
    public sorOrderTest(
        requestParameters: SorOrderTestRequest
    ): Promise<WebsocketApiResponse<SorOrderTestResponse>> {
        return this.websocketBase.sendMessage<SorOrderTestResponse>(
            '/sor.order.test'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }
}

export enum OrderCancelCancelRestrictionsEnum {
    ONLY_NEW = 'ONLY_NEW',
    NEW = 'NEW',
    ONLY_PARTIALLY_FILLED = 'ONLY_PARTIALLY_FILLED',
    PARTIALLY_FILLED = 'PARTIALLY_FILLED',
}

export enum OrderCancelReplaceCancelReplaceModeEnum {
    STOP_ON_FAILURE = 'STOP_ON_FAILURE',
    ALLOW_FAILURE = 'ALLOW_FAILURE',
}

export enum OrderCancelReplaceSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderCancelReplaceTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderCancelReplaceTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderCancelReplaceNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum OrderCancelReplaceSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderCancelReplaceCancelRestrictionsEnum {
    ONLY_NEW = 'ONLY_NEW',
    NEW = 'NEW',
    ONLY_PARTIALLY_FILLED = 'ONLY_PARTIALLY_FILLED',
    PARTIALLY_FILLED = 'PARTIALLY_FILLED',
}

export enum OrderCancelReplaceOrderRateLimitExceededModeEnum {
    DO_NOTHING = 'DO_NOTHING',
    CANCEL_ONLY = 'CANCEL_ONLY',
}

export enum OrderCancelReplacePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderCancelReplacePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderListPlaceSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceStopLimitTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum OrderListPlaceSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderListPlaceOcoSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOcoAboveTypeEnum {
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    LIMIT_MAKER = 'LIMIT_MAKER',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOcoBelowTypeEnum {
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOcoAboveTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOcoAbovePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOcoAbovePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOcoBelowTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOcoBelowPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOcoBelowPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOcoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum OrderListPlaceOcoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderListPlaceOpoWorkingTypeEnum {
    LIMIT = 'LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOpoWorkingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOpoPendingTypeEnum {
    LIMIT = 'LIMIT',
    MARKET = 'MARKET',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOpoPendingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOpoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum OrderListPlaceOpoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderListPlaceOpoWorkingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpoWorkingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpoWorkingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOpoPendingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpoPendingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpoPendingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOpocoWorkingTypeEnum {
    LIMIT = 'LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOpocoWorkingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOpocoPendingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOpocoPendingAboveTypeEnum {
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    LIMIT_MAKER = 'LIMIT_MAKER',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOpocoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum OrderListPlaceOpocoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderListPlaceOpocoWorkingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpocoWorkingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpocoWorkingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOpocoPendingAboveTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpocoPendingAbovePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpocoPendingAbovePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOpocoPendingBelowTypeEnum {
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOpocoPendingBelowTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOpocoPendingBelowPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOpocoPendingBelowPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtoWorkingTypeEnum {
    LIMIT = 'LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOtoWorkingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOtoPendingTypeEnum {
    LIMIT = 'LIMIT',
    MARKET = 'MARKET',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOtoPendingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOtoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum OrderListPlaceOtoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderListPlaceOtoWorkingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtoWorkingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtoWorkingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtoPendingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtoPendingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtoPendingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtocoWorkingTypeEnum {
    LIMIT = 'LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
}

export enum OrderListPlaceOtocoWorkingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOtocoPendingSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderListPlaceOtocoPendingAboveTypeEnum {
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    LIMIT_MAKER = 'LIMIT_MAKER',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOtocoNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum OrderListPlaceOtocoSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderListPlaceOtocoWorkingTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtocoWorkingPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtocoWorkingPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtocoPendingAboveTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtocoPendingAbovePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtocoPendingAbovePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderListPlaceOtocoPendingBelowTypeEnum {
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
}

export enum OrderListPlaceOtocoPendingBelowTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
}

export enum OrderListPlaceOtocoPendingBelowPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
}

export enum OrderListPlaceOtocoPendingBelowPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
}

export enum OrderPlaceSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderPlaceTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderPlaceTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderPlaceNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum OrderPlaceSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderPlacePegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderPlacePegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderTestSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum OrderTestTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderTestTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderTestNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum OrderTestSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderTestPegPriceTypeEnum {
    PRIMARY_PEG = 'PRIMARY_PEG',
    MARKET_PEG = 'MARKET_PEG',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum OrderTestPegOffsetTypeEnum {
    PRICE_LEVEL = 'PRICE_LEVEL',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum SorOrderPlaceSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum SorOrderPlaceTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum SorOrderPlaceTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum SorOrderPlaceNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum SorOrderPlaceSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum SorOrderTestSideEnum {
    BUY = 'BUY',
    SELL = 'SELL',
}

export enum SorOrderTestTypeEnum {
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
    STOP_LOSS = 'STOP_LOSS',
    STOP_LOSS_LIMIT = 'STOP_LOSS_LIMIT',
    TAKE_PROFIT = 'TAKE_PROFIT',
    TAKE_PROFIT_LIMIT = 'TAKE_PROFIT_LIMIT',
    LIMIT_MAKER = 'LIMIT_MAKER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum SorOrderTestTimeInForceEnum {
    GTC = 'GTC',
    IOC = 'IOC',
    FOK = 'FOK',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}

export enum SorOrderTestNewOrderRespTypeEnum {
    ACK = 'ACK',
    RESULT = 'RESULT',
    FULL = 'FULL',
    MARKET = 'MARKET',
    LIMIT = 'LIMIT',
}

export enum SorOrderTestSelfTradePreventionModeEnum {
    NONE = 'NONE',
    EXPIRE_TAKER = 'EXPIRE_TAKER',
    EXPIRE_MAKER = 'EXPIRE_MAKER',
    EXPIRE_BOTH = 'EXPIRE_BOTH',
    DECREMENT = 'DECREMENT',
    TRANSFER = 'TRANSFER',
    NON_REPRESENTABLE = 'NON_REPRESENTABLE',
}
